/**
 * Shimmer placeholders shown while data loads — used instead of spinners/text so
 * the layout doesn't jump when content arrives. Compose `Skeleton` for one-offs,
 * or use `TableSkeleton` / `CardSkeleton` for the common page shells.
 */

export function Skeleton({ className = '' }: { className?: string }) {
  return <div aria-hidden className={`animate-pulse rounded-[6px] bg-ink-100 ${className}`} />;
}

/** Table-shaped placeholder matching the DataTable shell (toolbar + rows). */
export function TableSkeleton({
  rows = 8,
  cols = 5,
}: {
  rows?: number;
  cols?: number;
}) {
  return (
    <div role="status" aria-busy="true" aria-label="Loading" className="panel overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-3 border-b border-surface-line px-4 py-3.5">
        <Skeleton className="h-10 w-64 !rounded-full" />
        <div className="ml-auto flex gap-2">
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-8 w-20" />
        </div>
      </div>
      {/* Header row */}
      <div className="flex items-center gap-4 border-b border-surface-line bg-surface-sunk px-4 py-3">
        {Array.from({ length: cols }).map((_, c) => (
          <Skeleton key={c} className={`h-3 ${c === 0 ? 'w-24' : 'flex-1'} !bg-ink-200/70`} />
        ))}
      </div>
      {/* Rows */}
      <div className="divide-y divide-surface-line">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex items-center gap-4 px-4 py-[18px]">
            {Array.from({ length: cols }).map((_, c) => (
              <Skeleton key={c} className={`h-4 ${c === 0 ? 'w-40' : 'flex-1'}`} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Just the shimmer rows (no border/container) — drop inside a table wrapper that
 * already has its own border, e.g. the Delivery / Payouts pages.
 */
export function SkeletonRows({
  rows = 6,
  cols = 5,
}: {
  rows?: number;
  cols?: number;
}) {
  return (
    <div role="status" aria-busy="true" aria-label="Loading" className="divide-y divide-surface-line">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4 px-4 py-[18px]">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className={`h-4 ${c === 0 ? 'w-40' : 'flex-1'}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Card-grid placeholder for settings / detail pages (e.g. Integrations). */
export function CardSkeleton({ cards = 3 }: { cards?: number }) {
  return (
    <div role="status" aria-busy="true" aria-label="Loading" className="space-y-4">
      {Array.from({ length: cards }).map((_, i) => (
        <div key={i} className="panel overflow-hidden">
          <div className="flex items-center gap-3 border-b border-surface-line p-5">
            <Skeleton className="h-10 w-10 !rounded-[10px]" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-64" />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-5 p-5 md:grid-cols-2">
            {Array.from({ length: 4 }).map((_, f) => (
              <div key={f} className="space-y-2">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-[42px] w-full !rounded-sm" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
